import type { ReactNode } from 'react'

/**
 * The 40×40 asset badge on every balance row: radius 22 with a `white/8%`
 * border. Toman is the flag; every other asset is its brand colour with the
 * coin's mark on top.
 *
 * Three sources, in order: the catalogue's `icon_url` when the backend sends
 * one, then a drawn mark, then the symbol's first letter. The marks are
 * original paths on the same 24-grid as `Icon`, for the same reason §8.7 gives
 * there — the logo files are trademarks with unconfirmed licences, so nothing
 * is copied and `icon_url` remains the production source.
 */
interface CoinIconProps {
  symbol: string
  iconUrl?: string | null
  size?: number
  className?: string
}

const BRAND: Record<string, string> = {
  BTC: '#f7931a',
  ETH: '#627eea',
  USDT: '#26a17b',
  USDC: '#2775ca',
  XRP: '#1b1c1e',
  DOGE: '#c2a633',
  TRX: '#eb0029',
  BNB: '#f3ba2f',
  ADA: '#0033ad',
  SOL: '#9945ff',
  LTC: '#345d9d',
  PAXG: '#b8860b',
}

/** Where the badge letter is not simply the symbol's first character. */
const GLYPH: Record<string, string> = { USDT: 'T', USDC: 'C' }

/** Drawn on a 24×24 grid, filled white, centred in the badge. */
const MARK: Record<string, ReactNode> = {
  // ₿ — stems above and below a two-lobed B.
  BTC: (
    <>
      <path d="M10.1 3h1.5v3h-1.5zM13 3h1.5v3H13zM10.1 18h1.5v3h-1.5zM13 18h1.5v3H13z" />
      <path d="M7.6 5.9h5.1a3.3 3.3 0 0 1 1.9 6 3.4 3.4 0 0 1-1.4 6.2H7.6zm2.6 2.3v3.1h2.2a1.55 1.55 0 0 0 0-3.1zm0 5.2v3.4h2.6a1.7 1.7 0 0 0 0-3.4z" />
    </>
  ),
  // The octahedron, seen edge-on: upper diamond over its lower reflection.
  ETH: (
    <>
      <path d="M12 2.2 6.2 11.9 12 15.3l5.8-3.4z" opacity=".85" />
      <path d="M12 16.5 6.2 13.1 12 21.8l5.8-8.7z" />
    </>
  ),
  // ₮ — the T with its stem crossed.
  USDT: (
    <>
      <path d="M5.4 5.2h13.2v3.1h-4.9v10.5h-3.4V8.3H5.4z" />
      <path d="M7.9 10.4h8.2v1.9H7.9z" />
    </>
  ),
  USDC: <path d="M5.4 5.2h13.2v3.1h-4.9v10.5h-3.4V8.3H5.4z" />,
  // Two chevrons meeting in the middle.
  XRP: (
    <>
      <path d="M5.6 4.6 12 10.9l6.4-6.3 2 2L12 15 3.6 6.6z" />
      <path d="M5.6 19.4 3.6 17.4 12 9l8.4 8.4-2 2L12 13.1z" opacity=".8" />
    </>
  ),
  // Ð — a D with the crossbar through its stem.
  DOGE: (
    <>
      <path d="M8.3 4.6h4.4a7.4 7.4 0 0 1 0 14.8H8.3zm3 3v8.8h1.2a4.4 4.4 0 0 0 0-8.8z" />
      <path d="M5.6 10.6h7.1v2.3H5.6z" />
    </>
  ),
  // Two gold bars, the smaller one stacked on the larger.
  PAXG: (
    <>
      <path d="M8.1 9.4 9.7 5.2h4.6l1.6 4.2z" opacity=".85" />
      <path d="M3.6 18.8 6.1 11.4h11.8l2.5 7.4z" />
    </>
  ),
}

export function CoinIcon({ symbol, iconUrl, size = 40, className = '' }: CoinIconProps) {
  const style = { width: size, height: size, borderRadius: size * 0.55 }

  if (symbol === 'IRR' || symbol === 'IRT') {
    return (
      <span
        style={style}
        className={`flex shrink-0 flex-col overflow-hidden border border-line-8 ${className}`}
        aria-hidden="true"
      >
        <span className="flex-1 bg-[#239f40]" />
        <span className="flex-1 bg-[#f0f0f0]" />
        <span className="flex-1 bg-[#da0000]" />
      </span>
    )
  }

  const brand = BRAND[symbol]
  const mark = MARK[symbol]

  return (
    <span
      style={{ ...style, backgroundColor: brand }}
      className={`flex shrink-0 items-center justify-center overflow-hidden border border-line-8 ${
        brand ? '' : 'bg-surface-8'
      } ${className}`}
      aria-hidden="true"
    >
      {iconUrl ? (
        <img src={iconUrl} alt="" className="size-full object-cover" loading="lazy" />
      ) : mark ? (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          style={{ width: size * 0.62, height: size * 0.62 }}
          className="text-white"
        >
          {mark}
        </svg>
      ) : (
        <span className="text-[0.55em] font-bold text-ink">
          {GLYPH[symbol] ?? symbol.slice(0, 1)}
        </span>
      )}
    </span>
  )
}
